import { useCallback, useMemo } from "react";

import { useHistory } from "react-router-dom";

import { useAppDispatch } from "../../../app/store";
import { useAuth } from "../../../hooks";
import { Role } from "../../../models/Shared/Role";
import {
  useDeleteCountyCommitteeMutation,
  useDeleteInstitutionCommitteeMutation,
  useDeleteRegionalCommitteeMutation,
  useDeleteMinistryCommitteeMutation,
} from "../../../queries/committeeQuery";
import {
  committeeListRoute,
  countyCommitteeListRoute,
  regionalCommitteeListRoute,
  ministryCommitteeListRoute,
} from "../../../routes/routes";
import { resetIsEdited } from "../../../slices/applicationSlice";

interface UseDeleteCommitteeProps {
  isEnabled: boolean;
  committeeType: "county" | "institution" | "regional" | "ministry";
  committeeId: number;
}

export const useDeleteCommittee = ({
  isEnabled,
  committeeType,
  committeeId,
}: UseDeleteCommitteeProps) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { hasRoleAccess } = useAuth();

  const [deleteCountyCommittee] = useDeleteCountyCommitteeMutation();
  const [deleteInstitutionCommittee] = useDeleteInstitutionCommitteeMutation();
  const [deleteRegionalCommittee] = useDeleteRegionalCommitteeMutation();
  const [deleteMinistryCommittee] = useDeleteMinistryCommitteeMutation();

  const isDeleteEnabled = useMemo(() => {
    return (
      hasRoleAccess({
        whitelist: [Role.CAS, Role.Helpdesk],
      }) && isEnabled
    );
  }, [hasRoleAccess, isEnabled]);

  const handleDeleteCommittee = useCallback(async () => {
    if (!isDeleteEnabled || committeeId <= 0) return;

    try {
      // Reset edit state before navigating to prevent confirmation popup
      dispatch(resetIsEdited());

      let redirectUrl;

      switch (committeeType) {
        case "county":
          await deleteCountyCommittee(committeeId).unwrap();
          redirectUrl = countyCommitteeListRoute;
          break;
        case "institution":
          await deleteInstitutionCommittee(committeeId).unwrap();
          redirectUrl = committeeListRoute;
          break;
        case "regional":
          await deleteRegionalCommittee(committeeId).unwrap();
          redirectUrl = regionalCommitteeListRoute;
          break;
        case "ministry":
          await deleteMinistryCommittee(committeeId).unwrap();
          redirectUrl = ministryCommitteeListRoute;
          break;
        default:
          throw new Error("Invalid committee type");
      }

      console.log("Delete successful");

      history.push(redirectUrl);
    } catch (error) {
      console.error("Error deleting committee:", error);
    }
  }, [
    isDeleteEnabled,
    committeeId,
    committeeType,
    deleteCountyCommittee,
    deleteInstitutionCommittee,
    deleteRegionalCommittee,
    deleteMinistryCommittee,
    history,
    dispatch,
  ]);

  return { handleDeleteCommittee, isDeleteEnabled };
};
